import type { IAsyncApiSpecification } from '@internal/schemas-common';
import type { IMessage } from '../schemas/IMessage.interface';
import { MessageStatus } from '../enums/MessageStatus.enum';
import { MessageType } from '../enums/MessageType.enum';

type AsyncApiSpecification = Pick<IAsyncApiSpecification, 'specification'>['specification'];
type AsyncApiChannel = Exclude<AsyncApiSpecification['channels'], undefined>[string];
type AsyncApiMessage = Exclude<AsyncApiChannel['messages'], undefined>[string];
type AsyncApiOperation = Exclude<AsyncApiSpecification['operations'], undefined>[string];
type CommonMetadata = Pick<IMessage, 'confluenceUrl' | 'ownerEmail'>;
type ScalarDataMessage = Exclude<AsyncApiMessage['x-scalar-data-message'], undefined>;
type ScalarDataMessageFields = Pick<ScalarDataMessage, 'status' | 'endOfSupportOn'>;

const VALID_MESSAGE_TYPES = new Set(Object.values(MessageType));

export function transformAsyncApiSpecificationsToMessages(
  specifications: IAsyncApiSpecification[],
): IMessage[] {
  const allMessages: IMessage[] = [];

  specifications.forEach(specification => {
    const messages = extractMessagesFromAsyncApiSpecification(specification);

    allMessages.push(...messages);
  });

  return deduplicateMessagesByName(allMessages);
}

function deduplicateMessagesByName(messages: IMessage[]): IMessage[] {
  const messagesByName = new Map<string, IMessage[]>();

  for (const message of messages) {
    const existing = messagesByName.get(message.name) || [];
    existing.push(message);
    messagesByName.set(message.name, existing);
  }

  const deduplicated: IMessage[] = [];
  for (const duplicates of messagesByName.values()) {
    if (duplicates.length === 1) {
      deduplicated.push(duplicates[0]);
      continue;
    }

    let bestMessage = duplicates[0];
    let bestScore = scoreMessage(bestMessage);

    for (let i = 1; i < duplicates.length; i++) {
      const currentScore = scoreMessage(duplicates[i]);
      if (currentScore > bestScore) {
        bestScore = currentScore;
        bestMessage = duplicates[i];
      }
    }

    deduplicated.push(bestMessage);
  }

  return deduplicated;
}

function scoreMessage(message: IMessage): number {
  let score = 0;
  if (message.status === MessageStatus.APPROVED) score += 3;
  if (message.ownerEmail) score += 2;
  if (message.payload) score += 2;
  if (message.headers) score += 2;
  if (message.examples?.length) score += Math.min(message.examples.length, 2);
  if (message.description) score += 1;
  if (message.confluenceUrl) score += 1;
  if (message.ingestionApps?.length) score += 1;
  return score;
}

function extractMessagesFromAsyncApiSpecification(
  asyncApiSpec: IAsyncApiSpecification,
): IMessage[] {
  const messages: IMessage[] = [];

  const { entityRef, specification } = asyncApiSpec;

  if (!specification) {
    return messages;
  }

  const commonMetadata = extractCommonMetadata(specification);
  const messageNameSet = new Set<IMessage['name']>();

  // Extract from AsyncAPI 3.x channels format
  extractMessagesFromChannels(
    specification.channels,
    entityRef,
    commonMetadata,
    messages,
    messageNameSet,
  );

  // Extract from AsyncAPI 2.x components format
  extractMessagesFromComponents(
    specification.components?.messages,
    entityRef,
    commonMetadata,
    messages,
    messageNameSet,
  );

  // Extract from AsyncAPI 3.x operations format (lakehouse pattern)
  extractMessagesFromOperations(
    specification.operations,
    entityRef,
    commonMetadata,
    messages,
    messageNameSet,
  );

  return messages;
}

function extractCommonMetadata(specification: AsyncApiSpecification): CommonMetadata {
  const info = specification.info || {};
  return {
    ...(info.externalDocs?.url && { confluenceUrl: info.externalDocs.url }),
    ...(info.contact?.email && { ownerEmail: info.contact.email }),
  };
}

function extractMessagesFromChannels(
  channels: Record<string, AsyncApiChannel> | undefined,
  entityRef: IMessage['entityRef'],
  commonMetadata: CommonMetadata,
  messages: IMessage[],
  messageNameSet: Set<IMessage['name']>,
): void {
  if (!channels) {
    return;
  }

  Object.values(channels).forEach((channel: AsyncApiChannel) => {
    if (channel?.messages) {
      Object.entries(channel.messages).forEach(
        ([messageKey, messageSpecification]: [string, AsyncApiMessage]) => {
          const messageName = messageSpecification?.name || messageKey;
          addMessageIfNotExists(
            messageName,
            messageSpecification,
            channel,
            entityRef,
            commonMetadata,
            messages,
            messageNameSet,
          );
        },
      );
    }
  });
}

function extractMessagesFromComponents(
  componentsMessages: Record<string, AsyncApiMessage> | undefined,
  entityRef: IMessage['entityRef'],
  commonMetadata: CommonMetadata,
  messages: IMessage[],
  messageNameSet: Set<IMessage['name']>,
): void {
  if (!componentsMessages) {
    return;
  }

  Object.entries(componentsMessages).forEach(([messageName, messageSpecification]: [string, AsyncApiMessage]) => {
    addMessageIfNotExists(
      messageName,
      messageSpecification,
      undefined,
      entityRef,
      commonMetadata,
      messages,
      messageNameSet,
    );
  });
}

function extractMessagesFromOperations(
  operations: Record<string, AsyncApiOperation> | undefined,
  entityRef: string,
  commonMetadata: CommonMetadata,
  messages: IMessage[],
  messageNameSet: Set<IMessage['name']>,
): void {
  if (!operations) {
    return;
  }

  Object.values(operations).forEach((operation: AsyncApiOperation) => {
    const channel = operation?.channel;
    if (channel && typeof channel !== 'string' && channel.messages) {
      Object.entries(channel.messages).forEach(
        ([messageKey, messageSpecification]: [string, AsyncApiMessage]) => {
          const messageName = messageSpecification?.name || messageKey;
          addMessageIfNotExists(
            messageName,
            messageSpecification,
            channel,
            entityRef,
            commonMetadata,
            messages,
            messageNameSet,
          );
        },
      );
    }
  });
}

function isScalarMessage(
  messageSpecification: AsyncApiMessage
): boolean {
  if (!messageSpecification) {
    return false;
  }

  if (messageSpecification.traits && Array.isArray(messageSpecification.traits)) {
    for (const trait of messageSpecification.traits) {
      if (trait?.['x-scalar-data-message']) {
        return true;
      }
    }
  }

  return false;
}

function addMessageIfNotExists(
  messageName: IMessage['name'],
  messageSpecification: AsyncApiMessage,
  channel: AsyncApiChannel | undefined,
  entityRef: IMessage['entityRef'],
  commonMetadata: CommonMetadata,
  messages: IMessage[],
  messageNameSet: Set<IMessage['name']>,
): void {
  if (messageNameSet.has(messageName)) {
    return;
  }
  
  // Only include messages with a dot in the name (e.g., "position.registered" but filter out positionRegistered)
  if (!messageName.includes('.')) {
    return;
  }

  // Only include SCALAR messages (messages with x-scalar-data-message property)
  if (!isScalarMessage(messageSpecification)) {
    return;
  }

  const ingestionApps = extractIngestionAppsFromMessage(messageSpecification, channel);
  const description = extractDescriptionFromMessage(messageSpecification, channel);
  const { examples, payload, headers } = extractMessageDetailData(messageSpecification);

  const message: IMessage = {
    name: messageName,
    entityRef,
    type: determineMessageTypeFromName(messageName),
    status: determineMessageStatusFromSpecification(messageSpecification, channel),
    ...(description && { description }),
    ...(commonMetadata.confluenceUrl && { confluenceUrl: commonMetadata.confluenceUrl }),
    ...(commonMetadata.ownerEmail && { ownerEmail: commonMetadata.ownerEmail }),
    ...(ingestionApps && { ingestionApps }),
    ...(payload && { payload }),
    ...(headers && { headers }),
    ...(examples && examples.length > 0 && { examples }),
  };

  messages.push(message);
  messageNameSet.add(messageName);
}

function extractIngestionAppsFromMessage(messageSpecification: AsyncApiMessage, channel?: AsyncApiChannel): IMessage['ingestionApps'] {
  if (!messageSpecification) {
    return undefined;
  }

  // Check for x-scalar-data-message (most common case)
  if (messageSpecification['x-scalar-data-message']?.ingestionApps) {
    return messageSpecification['x-scalar-data-message'].ingestionApps;
  }

  if (channel?.['x-scalar-data-message']?.ingestionApps) {
    return channel['x-scalar-data-message'].ingestionApps;
  }

  // Check traits array (AsyncAPI 3.x pattern)
  if (messageSpecification.traits && Array.isArray(messageSpecification.traits)) {
    for (const trait of messageSpecification.traits) {
      if (trait?.['x-scalar-data-message']?.ingestionApps) {
        return trait['x-scalar-data-message'].ingestionApps;
      }
    }
  }

  return undefined;
}

function extractDescriptionFromMessage(messageSpecification: AsyncApiMessage, channel?: AsyncApiChannel): IMessage['description'] | undefined {
  if (!messageSpecification) {
    return undefined;
  }

  if (messageSpecification.description) {
    return messageSpecification.description;
  }

  // Check traits array for description (AsyncAPI 3.x pattern)
  if (messageSpecification.traits && Array.isArray(messageSpecification.traits)) {
    for (const trait of messageSpecification.traits) {
      if (trait?.description) {
        return trait.description;
      }
    }
  }

  // Check channel-level traits as fallback
  if (channel?.traits && Array.isArray(channel.traits)) {
    for (const trait of channel.traits) {
      if (trait?.description) {
        return trait.description;
      }
    }
  }

  return undefined;
}

function extractMessageDetailData(messageSpecification: AsyncApiMessage): {
  examples: IMessage['examples'];
  payload: IMessage['payload'];
  headers: IMessage['headers'];
} {
  if (!messageSpecification) {
    return { examples: undefined, payload: undefined, headers: undefined };
  }

  let examples: IMessage['examples'] = messageSpecification.examples || [];
  let headers: IMessage['headers'] = messageSpecification.headers;

  const payload = messageSpecification.payload;

  // Merge examples and headers from traits in a single pass (AsyncAPI 3.x pattern)
  if (messageSpecification.traits && Array.isArray(messageSpecification.traits)) {
    for (const trait of messageSpecification.traits) {
      if (trait?.examples && Array.isArray(trait.examples)) {
        examples = [...examples, ...trait.examples];
      }

      if (trait?.headers) {
        if (!headers) {
          headers = trait.headers;
        } else {
          const messageHeadersId = headers.$id;
          headers = {
            ...trait.headers,
            ...headers,
            properties: {
              ...(trait.headers.properties || {}),
              ...(headers.properties || {}),
            },
            required: Array.from(
              new Set([
                ...(trait.headers.required || []),
                ...(headers.required || []),
              ]),
            ),
          };

          if (messageHeadersId) {
            headers.$id = messageHeadersId;
          }

          if (!headers.required || headers.required.length === 0) {
            delete headers.required;
          }
        }
      }
    }
  }

  return {
    examples: examples.length > 0 ? examples : undefined,
    payload,
    headers,
  };
}

function determineMessageTypeFromName(messageName: IMessage['name']): MessageType {
  const prefix = messageName.split('.')[0]?.toLowerCase();

  // Typesafe because we do runtime check
  if (VALID_MESSAGE_TYPES.has(prefix as MessageType)) {
    return prefix as MessageType;
  }

  return MessageType.MESSAGE;
}

function determineMessageStatusFromSpecification(
  messageSpecification: AsyncApiMessage,
  channel?: AsyncApiChannel,
): MessageStatus {
  if (!messageSpecification) {
    return MessageStatus.UNKNOWN;
  }

  // Check for x-scalar-data-message extension at message level, then channel level
  const scalarDataMessage = (messageSpecification['x-scalar-data-message'] || channel?.['x-scalar-data-message']);

  if (scalarDataMessage?.status) {
    const resolved = resolveStatusWithEndOfSupportDate(
      scalarDataMessage.status,
      scalarDataMessage.endOfSupportOn,
    );
    if (resolved !== MessageStatus.UNKNOWN) {
      return resolved;
    }
  }

  // Check traits array for x-scalar-data-message (AsyncAPI 3.x pattern)
  if (messageSpecification.traits && Array.isArray(messageSpecification.traits)) {
    for (const trait of messageSpecification.traits) {
      const traitExtension = trait?.['x-scalar-data-message'];
      if (traitExtension?.status) {
        const resolved = resolveStatusWithEndOfSupportDate(
          traitExtension.status,
          traitExtension.endOfSupportOn,
        );
        if (resolved !== MessageStatus.UNKNOWN) {
          return resolved;
        }
      }
    }
  }

  // Check for custom extension x-status at message level (fallback)
  if (messageSpecification['x-status']) {
    const status = stringToStatusEnum(messageSpecification['x-status']);
    if (status !== MessageStatus.UNKNOWN) {
      return status;
    }
  }

  // Check for custom extension x-lifecycle at message level (fallback)
  if (messageSpecification['x-lifecycle']) {
    const status = stringToStatusEnum(messageSpecification['x-lifecycle']);
    if (status !== MessageStatus.UNKNOWN) {
      return status;
    }
  }

  if (messageSpecification.deprecated === true) {
    return MessageStatus.DEPRECATED;
  }

  return MessageStatus.UNKNOWN;
}

function resolveStatusWithEndOfSupportDate(
  statusValue: ScalarDataMessageFields['status'],
  endOfSupportOn?: ScalarDataMessageFields['endOfSupportOn'],
): MessageStatus {
  if (!statusValue) {
    return MessageStatus.UNKNOWN;
  }

  const status = stringToStatusEnum(statusValue);

  if (status === MessageStatus.DEPRECATED && endOfSupportOn) {
    if (hasDatePassed(endOfSupportOn)) {
      return MessageStatus.END_OF_SUPPORT;
    }
  }

  return status;
}

function stringToStatusEnum(value: string): MessageStatus {
  if (!value || typeof value !== 'string') {
    return MessageStatus.UNKNOWN;
  }

  // lowercase, trim, replace underscores/hyphens with spaces
  const normalized = value.toLowerCase().trim().replace(/[_-]/g, ' ');

  const match = Object.values(MessageStatus).find(
    status => status === normalized
  );

  return match ?? MessageStatus.UNKNOWN;
}

function hasDatePassed(endOfSupportOn: ScalarDataMessageFields['endOfSupportOn']): boolean {
  if (!endOfSupportOn) {
    return false;
  }

  const endDate = new Date(endOfSupportOn);
  const now = new Date();
  return now >= endDate;
}
